<template>
  <v-container>
    <!-- Subscription Management Dialog -->
    <v-dialog v-model="dialog.open" persistent max-width="500">
      <v-card rounded="lg" elevation="0">
        <div class="card-section-header">
          <div class="d-flex align-center justify-space-between flex-wrap ga-3">
            <div class="d-flex align-center ga-3">
              <v-avatar color="brand-color" variant="tonal" size="40" rounded="lg">
                <v-icon size="18" color="brand-color">mdi-wallet</v-icon>
              </v-avatar>
              <h2 class="section-title">Subscription Management</h2>
            </div>
            <v-btn icon="mdi-close" variant="text" size="small" color="outlined-color" @click="closeDialog" />
          </div>
        </div>
        <v-card-text class="pa-4">
          <!-- Plan details section -->
          <v-list density="compact" class="pa-0 mb-2">
            <v-list-item class="px-0">
              <template v-slot:append>
                <v-chip :color="statusChipColor" class="font-weight-medium" size="small" rounded="lg">
                  {{ statusText }}
                </v-chip>
              </template>
              <v-list-item-title class="font-weight-bold">
                {{ planName }}
              </v-list-item-title>
              <v-list-item-subtitle class="font-weight-medium">
                {{ planPrice }}
              </v-list-item-subtitle>
            </v-list-item>
          </v-list>
          <!-- Date information for subscription -->
          <span v-if="hasSubscription"
            :class="['text-body-small d-flex align-center gc-1 mt-1', 'text-' + statusChipColor]">
            <v-icon size="14" :icon="dateInfoIcon" :color="statusChipColor"></v-icon>
            {{ subscriptionDateInfo }}
          </span>

          <!-- Cancelled subscription (grace) - informational -->
          <div class="mt-1 mb-0" v-if="showInfoCancelledSubscription">
            <v-alert color="danger-color" density="compact" variant="tonal" rounded="lg" class="mt-3 pl-4 pr-2 py-1 body-text-xs">
              <div class="text-danger-color">
                Your subscription is currently in a grace period. You will have access to all features until the end
                of the grace period.
                After the grace period ends access will be removed. You can subscribe again to restore access. To know
                more about the plan cancellation,
                <a :href="helpArticles.PLAN_CANCELLATION" target="_blank" rel="noopener">
                  <b class="text-danger-color text-decoration-underline font-weight-bold">click here</b>
                </a>.
              </div>
            </v-alert>
          </div>

          <!-- Alert message section for upgrade/downgrade/change info -->
          <v-alert v-if="showInfoUpgradeDowngrade" color="brand-color"
            class="mt-3 pl-4 pr-2 py-1 dialog-alert body-text-xs" density="compact" variant="tonal" rounded="lg">
            <span class="my-0 py-0 text-left">
              Click <b class="text-capitalize">
                {{ planChangeActionText }} Plan</b>
              below to update your plan to
              <b class="text-capitalize">
                {{ selectedPlanData?.name }} {{ selectedBillingCycle }}
              </b>.
            </span>
          </v-alert>

          <!-- Alert message section for manage subscription info -->
          <v-alert v-if="showInfoActiveSubscription" color="brand-color"
            class="mt-3 pl-4 pr-2 py-1 dialog-alert body-text-xs" density="compact" variant="tonal" rounded="lg">
            <span class="my-0 py-0 text-left">
              You can either pause or cancel your subscription. To know more about the difference between pause and
              cancel,
              <a :href="helpArticles.PAUSE_AND_CANCEL_SUBSCRIPTION" target="_blank" rel="noopener">
                <b class="text-brand-color text-decoration-underline hover-underline ">click here</b>
              </a>.
            </span>
          </v-alert>

          <!-- Alert message section for paused subscription info -->
          <v-alert v-if="showInfoPausedSubscription" color="brand-color"
            class="mt-3 pl-4 pr-2 py-1 dialog-alert body-text-xs" density="compact" variant="tonal" rounded="lg">
            <span class="my-0 py-0 text-left">
              You can either resume or cancel your subscription. To know more about the difference between resume and
              cancel,
              <a :href="helpArticles.PAUSE_AND_CANCEL_SUBSCRIPTION" target="_blank" rel="noopener">
                <b class="text-brand-color text-decoration-underline hover-underline ">click here</b>
              </a>.
            </span>
          </v-alert>
        </v-card-text>
        <v-divider></v-divider>
        <!-- Actions -->
        <v-card-actions class="justify-end pa-4">
          <v-row no-gutters>

            <v-col cols="12" align="center" class="pa-0">
              <!-- No subscription - Subscribe, or keep trialing a different plan instead -->
              <div v-if="!hasSubscription" class="d-flex w-100 ga-3 justify-end">
                <v-btn v-if="showChooseTrialInstead" class="text-none font-weight-bold text-capitalize flex-1-1"
                  color="outlined-color" prepend-icon="mdi-timer-outline" variant="outlined"
                  :loading="dialog.loader" :disabled="dialog.loader" @click="onChooseTrialInstead" height="40"
                  aria-label="Choose Trial Instead">
                  Choose Trial Instead
                </v-btn>
                <v-btn :class="['text-none font-weight-bold', showChooseTrialInstead ? 'text-capitalize flex-1-1' : '']"
                  color="brand-color" prepend-icon="mdi-cart-check"
                  variant="flat" :loading="dialog.loader" :disabled="dialog.loader" @click="onSubscribe" height="40"
                  aria-label="Subscribe Now">
                  Subscribe Now
                </v-btn>
              </div>

              <!-- Active subscription - Upgrade/Downgrade Plan -->
              <div v-if="showInfoUpgradeDowngrade" class="d-flex w-100 ga-3 mb-1">

                <v-btn class="text-none font-weight-bold text-capitalize flex-1-1" color="outlined-color"
                  prepend-icon="mdi-cart-check" variant="outlined" height="40" @click="dialog.open = false">
                  Keep Plan
                </v-btn>

                <v-btn class="text-none font-weight-bold text-capitalize flex-1-1" color="brand-color"
                  :prepend-icon="planChangeIcon" variant="flat" :loading="dialog.loader" :disabled="dialog.loader"
                  @click="handleUpgradeDowngrade" height="40">
                  {{ planChangeActionText }} Plan
                </v-btn>
              </div>

              <!-- Active subscription - Pause + Cancel (when managing current plan) -->
              <div v-if="showInfoActiveSubscription" class="d-flex w-100 ga-3 mb-1">

                <v-btn class="text-none font-weight-bold text-capitalize flex-1-1" color="danger-color"
                  prepend-icon="mdi-cart-remove" variant="outlined"
                  :disabled="confirmDialog.loader && confirmDialog.action?._actionType === CONFIRM_ACTION_TYPES.PAUSE"
                  @click="onCancel" height="40">
                  Cancel Subscription
                </v-btn>

                <v-btn class="text-none font-weight-bold text-capitalize flex-1-1" color="accent-color"
                  prepend-icon="mdi-cart-off" variant="flat"
                  :loading="confirmDialog.loader && confirmDialog.action?._actionType === CONFIRM_ACTION_TYPES.PAUSE"
                  :disabled="confirmDialog.loader && confirmDialog.action?._actionType === CONFIRM_ACTION_TYPES.PAUSE"
                  @click="onPause" height="40">
                  Pause Subscription
                </v-btn>
              </div>

              <!-- Paused subscription - Resume + Cancel -->
              <div v-if="showInfoPausedSubscription" class="d-flex w-100 ga-3 mb-1">

                <v-btn class="text-none font-weight-bold text-capitalize flex-1-1" color="danger-color"
                  prepend-icon="mdi-cart-remove" variant="outlined"
                  :disabled="dialog.loader === LOADER_STATES.RESUME || (confirmDialog.loader && confirmDialog.action?._actionType === CONFIRM_ACTION_TYPES.CANCEL)"
                  @click="onCancel" height="40">
                  Cancel Subscription
                </v-btn>

                <v-btn class="text-none font-weight-bold text-capitalize flex-1-1" color="primary"
                  prepend-icon="mdi-cart" variant="flat" :loading="dialog.loader === LOADER_STATES.RESUME"
                  :disabled="dialog.loader === LOADER_STATES.RESUME || (confirmDialog.loader && confirmDialog.action?._actionType === CONFIRM_ACTION_TYPES.CANCEL)"
                  @click="onResume" height="40">
                  Resume Subscription
                </v-btn>
              </div>

              <!-- Cancelled subscription (grace) - Close  -->
              <div v-if="showInfoCancelledSubscription" class="d-flex w-100 ga-3">
                <v-btn class="text-none font-weight-bold flex-1-1" color="outlined-color" variant="outlined"
                  prepend-icon="mdi-cart-remove" height="40"
                  :disabled="dialog.loader === LOADER_STATES.UNDO_CANCEL" @click="closeDialog"
                  aria-label="Keep Subscription Cancelled">
                  Keep Cancelled
                </v-btn>
                <v-btn v-if="canUndoCancellation" class="text-none font-weight-bold flex-1-1" color="primary"
                  variant="flat" height="40" prepend-icon="mdi-restore"
                  :loading="dialog.loader === LOADER_STATES.UNDO_CANCEL"
                  :disabled="dialog.loader === LOADER_STATES.UNDO_CANCEL" @click="onUndoCancellation"
                  aria-label="Don't Cancel Subscription">
                  Undo Cancellation
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirmation Dialog  Pause/Cancel -->
    <v-dialog v-model="confirmDialog.open" max-width="500" persistent>
      <v-card v-if="confirmDialog.action" rounded="lg" elevation="0">
        <div class="card-section-header">
          <div class="d-flex align-center ga-3">
            <v-avatar size="40" variant="tonal" rounded="lg"
              :color="confirmDialog.action?._actionType === CONFIRM_ACTION_TYPES.PAUSE ? 'accent-color' : 'danger'">
              <v-icon size="18"
                :color="confirmDialog.action?._actionType === CONFIRM_ACTION_TYPES.PAUSE ? 'accent-color' : 'danger'">{{
                  confirmDialog.action?._actionType === CONFIRM_ACTION_TYPES.PAUSE ? 'mdi-cart-off' : 'mdi-alert-circle'
                }}</v-icon>
            </v-avatar>
            <h2 class="section-title">Are you sure?</h2>
          </div>
        </div>
        <v-card-text class="pa-6">
          <v-form ref="confirmForm">
            <!-- Description & Reason -->
            <v-alert variant="tonal"
              :color="confirmDialog.action?._actionType === CONFIRM_ACTION_TYPES.PAUSE ? 'accent-color' : 'danger'"
              class="pl-4 pr-2 py-1 dialog-alert body-text-xs" density="compact" rounded="lg">
              <span class="my-0 py-0 text-left">
                Your plan will be {{ confirmDialog.action?._actionType === CONFIRM_ACTION_TYPES.PAUSE ?
                  'paused' : 'cancelled' }} immediately.
                You will still have access until the end of your billing period.
                <a :href="helpArticles.PAUSE_AND_CANCEL_SUBSCRIPTION" target="_blank" rel="noopener">
                  <b class="text-decoration-underline hover-underline body-text-xs font-weight-bold"
                    :class="confirmDialog.action?._actionType === CONFIRM_ACTION_TYPES.PAUSE ? 'text-accent-color' : 'text-danger-color'">
                    Need more info?
                  </b>
                </a>
              </span>
            </v-alert>

            <v-row class="my-0 py-0">
              <!-- Reason -->
              <v-col cols="12" class="mt-5 mb-0">
                <p class="text-body-medium text-grey-darken-2 mb-2">
                  Why do you want to {{ confirmDialog.action?._actionType === CONFIRM_ACTION_TYPES.PAUSE ? 'pause' :
                  'cancel' }} your subscription?
                </p>
                <v-autocomplete v-model="confirmDialog.reason" :items="PAUSE_CANCEL_REASONS"
                  placeholder="Select a reason" chips closable-chips clear-on-select
                  :rules="$valid([{ rule: 'required', msg: 'Please select a reason' }])" hide-details variant="outlined"
                  density="compact" rounded="lg"
                  :color="confirmDialog.action?._actionType === CONFIRM_ACTION_TYPES.PAUSE ? 'accent-color' : 'danger'"></v-autocomplete>
                <!-- Other reason text field -->
                <v-textarea v-if="confirmDialog.reason === OTHER_REASON" v-model="confirmDialog.otherReason"
                  label="Please specify your reason" placeholder="Tell us more..." variant="outlined" density="compact"
                  rounded="lg" class="mt-3" auto-grow rows="1"
                  :rules="$valid([{ rule: 'required', msg: 'Please provide a reason' }, { rule: 'between', min: 2, max: 100 }, { rule: 'alphaSpaces' }])"
                  hide-details="auto"></v-textarea>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="px-6 py-4 d-flex ga-3">
          <v-btn @click="closeBothDialogs" :disabled="confirmDialog.loader" variant="outlined" color="outlined-color"
            prepend-icon="mdi-cart-check" height="40" class="text-none font-weight-bold text-capitalize flex-1-1">
            No, Keep Subscription
          </v-btn>

          <v-tooltip v-if="!canProceedWithAction" location="top">
            <template #activator="{ props }">
              <div v-bind="props" class="flex-1-1">
                <v-btn :disabled="true" variant="flat" block height="40"
                  :color="confirmDialog.action?._actionType === CONFIRM_ACTION_TYPES.PAUSE ? 'accent-color' : 'danger'"
                  :prepend-icon="confirmDialog.action?._actionType === CONFIRM_ACTION_TYPES.PAUSE ? 'mdi-cart-off' : 'mdi-cart-remove'"
                  class="text-none font-weight-bold text-capitalize">
                  Yes, {{ confirmDialog.action?._actionType === CONFIRM_ACTION_TYPES.PAUSE ?
                    CONFIRM_ACTION_TYPES.PAUSE :
                    CONFIRM_ACTION_TYPES.CANCEL }} Subscription
                </v-btn>
              </div>
            </template>
            <span>Please select a reason to continue</span>
          </v-tooltip>
          <v-btn v-else :loading="confirmDialog.loader" :disabled="confirmDialog.loader" @click="confirmAction" :color="confirmDialog.action?._actionType === CONFIRM_ACTION_TYPES.PAUSE ?
            'accent-color' : 'danger'" variant="flat" class="text-none font-weight-bold text-capitalize flex-1-1"
            :prepend-icon="confirmDialog.action?._actionType === CONFIRM_ACTION_TYPES.PAUSE ? 'mdi-cart-off' : 'mdi-cart-remove'"
            height="40">
            Yes, {{ confirmDialog.action?._actionType === CONFIRM_ACTION_TYPES.PAUSE ? CONFIRM_ACTION_TYPES.PAUSE
              :
              CONFIRM_ACTION_TYPES.CANCEL }} Subscription
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import pricingMixin from '@/mixins/pricing'
import userMixin from '@/mixins/user'
import pricingHelpersMixin from '@/mixins/pricing/helpers'
import utilityMixin from '@/mixins/utility'
import { LOADER_STATES, CONFIRM_ACTION_TYPES, PAUSE_CANCEL_REASONS, OTHER_REASON } from '@/shared/constants/pricing/data'
import { PADDLE_PROVIDERS } from '@/shared/constants/pricing/paddle'

export default {
  name: 'PricingDialog',
  mixins: [pricingMixin, userMixin, pricingHelpersMixin, utilityMixin],
  props: {
    modelValue: Boolean,
    mode: String,
    selectedPlanKey: String,
    planType: Object
  },
  data() {
    return {
      CONFIRM_ACTION_TYPES, // Make constant available in component
      PAUSE_CANCEL_REASONS, // Make constant available in component
      OTHER_REASON, // Make constant available in component
      LOADER_STATES, // Make constant available in component
      dialog: {
        mode: null, // Will be set from mode prop
        open: false,
        loader: false
      },
      confirmDialog: {
        open: false,
        reason: null,
        otherReason: '',
        loader: false,
        action: null // Callback function for confirmation, contains action type info
      }
    }
  },
  computed: {
    planName() {
      // When has subscription: always show actual subscription plan
      if (this.hasSubscription) {
        return this.planHelpers_getPlanData(this.planHelpers_currentPlanKey).name
      }
      // When no subscription: show selected plan
      if (this.selectedPlanKey) {
        return this.planHelpers_getPlanData(this.selectedPlanKey).name
      }
      return 'Free'
    },
    planPrice() {
      // When has subscription: always show actual subscription plan price
      if (this.hasSubscription) {
        return this.planHelpers_getPlanData(this.planHelpers_currentPlanKey).formattedPrice
      }
      // When no subscription: show selected plan price
      if (this.selectedPlanKey) {
        return this.planHelpers_getPlanData(this.selectedPlanKey).formattedPrice
      }
      return '$0/month'
    },
    statusText() {
      return this.hasSubscription ? this.planHelpers_statusConfig.text : 'No Subscription'
    },
    statusChipColor() {
      return this.hasSubscription ? this.planHelpers_statusConfig.color : 'brand-color'
    },
    dateInfoIcon() {
      const status = this.planHelpers_subscriptionStatus
      if (status === this.SUBSCRIPTION_STATUS.ACTIVE) return 'mdi-calendar-clock'
      if (status === this.SUBSCRIPTION_STATUS.PAST_DUE) return 'mdi-alert-circle'
      if (status === this.SUBSCRIPTION_STATUS.PAUSED) return 'mdi-pause-circle'
      if (status === this.SUBSCRIPTION_STATUS.DELETED) return 'mdi-calendar-remove'
      return 'mdi-calendar'
    },
    subscriptionDateInfo() {
      if (!this.hasSubscription) return ''
      const sub = this.user?.plan?.subscription
      const status = this.planHelpers_subscriptionStatus

      const handlers = {
        [this.SUBSCRIPTION_STATUS.ACTIVE]: () => {
          const nextBilling = sub?.next_bill_date || this.user?.plan?.next_bill_date
          return nextBilling ? `Next billing date: ${this.planHelpers_formatDate(nextBilling)}` : ''
        },
        [this.SUBSCRIPTION_STATUS.PAST_DUE]: () => {
          const nextBilling = sub?.next_bill_date || this.user?.plan?.next_bill_date
          return nextBilling ? `Next billing date: ${this.planHelpers_formatDate(nextBilling)}` : 'Payment required'
        },
        [this.SUBSCRIPTION_STATUS.PAUSED]: () => {
          const pausedFrom = sub?.paused_from || this.user?.plan?.paused_from
          return pausedFrom ? `Paused From: ${this.planHelpers_formatDate(pausedFrom)}` : ''
        },
        [this.SUBSCRIPTION_STATUS.DELETED]: () => {
          const endsAt = sub?.cancel_scheduled_at || this.user?.plan?.cancel_scheduled_at
          return endsAt ? `Ends at: ${this.planHelpers_formatDate(endsAt)}` : ''
        }
      }
      const handler = handlers[status]
      return handler ? handler() : ''
    },
    subscriptionId() {
      return this.user?.plan?.subscription?.id
    },
    showInfoUpgradeDowngrade() {
      // Don't show upgrade/downgrade if paused or scheduled to pause (has paused_from)
      // According to Paddle docs: paused users can only resume or cancel
      if (this.userMixin_hasPausedSubscription) return false
      return this.showUpgradeDowngrade && this.planHelpers_subscriptionStatus === this.SUBSCRIPTION_STATUS.ACTIVE
    },
    showInfoActiveSubscription() {
      return !this.showUpgradeDowngrade && this.hasSubscription &&
        this.planHelpers_subscriptionStatus === this.SUBSCRIPTION_STATUS.ACTIVE &&
        !this.userMixin_isPastDueUser
    },
    showInfoPausedSubscription() {
      // Show paused UI if status is paused OR has paused_from (scheduled to pause)
      return this.hasSubscription && this.userMixin_hasPausedSubscription
    },
    showInfoCancelledSubscription() {
      return this.hasSubscription && this.hasCancelledSubscription
    },
    // Enables undo only for scheduled Paddle Billing cancellations still in the future.
    canUndoCancellation() {
      const sub = this.user?.plan?.subscription
      const endsAt = sub?.cancel_scheduled_at || this.user?.plan?.cancel_scheduled_at

      return Boolean(
        sub?.provider === PADDLE_PROVIDERS.BILLING &&
        endsAt &&
        new Date(endsAt).getTime() > Date.now()
      )
    },
    planChangeType() {
      // Determine plan change type: 'change', 'upgrade', or 'downgrade'
      if (!this.selectedPlanKey || !this.planHelpers_currentPlanKey) return null

      // Same tier, different billing cycle = change
      if (this.planHelpers_isSameTierDifferentBilling(this.planHelpers_currentPlanKey, this.selectedPlanKey)) {
        return LOADER_STATES.CHANGE
      }

      // Different tier = upgrade or downgrade based on price
      const currentPrice = this.planHelpers_getPlanPrice(this.planHelpers_currentPlanKey)
      const selectedPrice = this.planHelpers_getPlanPrice(this.selectedPlanKey)
      return selectedPrice > currentPrice ? LOADER_STATES.UPGRADE : LOADER_STATES.DOWNGRADE
    },
    planChangeActionText() {
      return this.planChangeType || LOADER_STATES.CHANGE
    },
    planChangeIcon() {
      const icons = {
        [LOADER_STATES.CHANGE]: 'mdi-sync',
        [LOADER_STATES.UPGRADE]: 'mdi-cart-arrow-up',
        [LOADER_STATES.DOWNGRADE]: 'mdi-cart-arrow-down'
      }
      return icons[this.planChangeType] || 'mdi-sync'
    },
    // Trialing (no live subscription) and looking at a different plan than the one being
    // trialed: offer to keep trialing that plan instead of going straight to checkout.
    // Excludes the same tier's other billing cycle (e.g. Silver Yearly while trialing Silver
    // Monthly) - trials only ever run monthly, so that's not "a different plan to trial",
    // it's just the paid version of the one already being trialed. Show Subscribe only.
    showChooseTrialInstead() {
      if (this.hasSubscription) return false
      if (!this.userMixin_hasActiveFreeTrialPlan) return false
      if (!this.selectedPlanKey) return false
      if (this.selectedPlanKey === this.planHelpers_currentPlanKey) return false
      if (this.planHelpers_isSameTierDifferentBilling(this.planHelpers_currentPlanKey, this.selectedPlanKey)) {
        return false
      }
      return true
    },
    showUpgradeDowngrade() {
      // Changing plans is only available on Paddle Billing - Classic subscribers can only pause/resume/cancel
      if (this.user?.plan?.subscription?.provider !== PADDLE_PROVIDERS.BILLING) return false
      // Show change plan when: has active subscription, in CHOOSE mode, and selected plan differs from current
      // Don't allow upgrade/downgrade if paused or scheduled to pause (has paused_from)
      // According to Paddle docs: paused users can only resume or cancel
      if (!this.hasSubscription) return false
      if (this.userMixin_hasPausedSubscription) return false
      if (this.planHelpers_subscriptionStatus !== this.SUBSCRIPTION_STATUS.ACTIVE) return false
      if (this.dialog.mode !== this.DIALOG_MODES.CHOOSE) return false
      if (!this.selectedPlanKey) return false

      // Simple comparison: selected public_id vs current public_id
      return this.selectedPlanKey !== this.planHelpers_currentPlanKey
    },
    selectedPlanData() {
      if (!this.selectedPlanKey) return { name: '', formattedPrice: '' }
      return this.planHelpers_getPlanData(this.selectedPlanKey)
    },
    selectedBillingCycle() {
      const key = this.planType?.isMonthly ? this.BILLING_CYCLES.MONTHLY.key : this.BILLING_CYCLES.YEARLY.key
      return key.charAt(0).toUpperCase() + key.slice(1)
    },
    canProceedWithAction() {
      if (!this.confirmDialog.reason) return false
      // If "Other reason" is selected, validate the text field
      if (this.confirmDialog.reason === OTHER_REASON) {
        const value = this.confirmDialog.otherReason
        // Required check
        if (!value || value.trim().length === 0) return false
        // Between check (2-100 characters)
        if (value.length < 2 || value.length > 100) return false
        // AlphaSpaces check
        const alphaSpacesRegex = /^[a-zA-Z\s]*$/
        if (!alphaSpacesRegex.test(value)) return false
      }
      return true
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(value) {
        this.dialog.open = value
      }
    },
    mode: {
      immediate: true,
      handler(value) {
        this.dialog.mode = value || this.DIALOG_MODES.CHOOSE
      }
    },
    'dialog.open'(value) {
      this.$emit('update:modelValue', value)
      if (value) {
        this.planMixin_initializePaddleBilling()
      } else {
        this.planMixin_resetSubscriptionStatus()
      }
    },
    'confirmDialog.reason'(value) {
      // Clear otherReason when reason changes away from "Other reason"
      if (value !== OTHER_REASON) {
        this.confirmDialog.otherReason = ''
      }
    }
  },
  methods: {
    closeDialog() {
      this.dialog = {
        mode: this.DIALOG_MODES.CHOOSE,
        open: false,
        loader: false
      }
      this.$emit('update:modelValue', false)
    },
    closeConfirmDialog() {
      // Close dialog first to prevent theme flash
      this.confirmDialog.open = false
      // Reset form validation immediately
      this.$refs.confirmForm?.resetValidation()
      // Clear values after dialog closes
      setTimeout(() => {
        this.confirmDialog.reason = null
        this.confirmDialog.otherReason = ''
        this.confirmDialog.loader = false
        this.confirmDialog.action = null
      }, 300) // Wait for dialog close animation
    },
    closeBothDialogs() {
      this.closeConfirmDialog()
      this.closeDialog()
    },
    showConfirmDialog(actionType, action) {
      // Store actionType in action function for ternary checks
      action._actionType = actionType
      this.confirmDialog = {
        open: true,
        reason: null,
        loader: false,
        action
      }
    },
    async confirmAction() {
      // Validate form before proceeding
      const formIsValid = await this.$refs.confirmForm?.validate()
      if (!formIsValid) {
        return
      }

      if (!this.confirmDialog.reason) {
        return
      }

      // If "Other reason" is selected, use the text from otherReason instead
      const reason = this.confirmDialog.reason === OTHER_REASON
        ? this.confirmDialog.otherReason
        : this.confirmDialog.reason

      this.confirmDialog.loader = true
      if (this.confirmDialog.action) {
        this.confirmDialog.action(reason)
      }
    },
    emitAlert(message, isError = false, timeout = 5000) {
      this.$emit('alert', { message, isError, timeout })
    },

    // Keep trialing, but a different plan (Bronze/Silver/Gold) - no checkout, just repoints
    // which plan's limits apply. Trials only ever run on the monthly plan, so a yearly card
    // selection still trials its monthly id.
    async onChooseTrialInstead() {
      const monthlyPlanId = this.selectedPlanData?.key?.monthly
      if (!monthlyPlanId) {
        this.emitAlert('No plan selected', true)
        return
      }

      this.dialog.loader = true
      try {
        await this.planMixin_switchTrialPlan(monthlyPlanId)
        this.emitAlert(`You're now trialing the ${this.selectedPlanData.name} plan.`)
        this.$emit('subscription-updated')
        this.closeBothDialogs()
      } catch (err) {
        this.emitAlert(err?.response?.data?.meta?.error_message ?? 'Unable to switch trial plan.', true)
      } finally {
        this.dialog.loader = false
      }
    },

    // Subscribe action: emit event for parent to handle checkout (product mapping lives elsewhere).
    async onSubscribe() {
      const publicId = this.dialog.mode === this.DIALOG_MODES.CHOOSE ?
        this.selectedPlanKey : this.planHelpers_currentPlanKey

      if (!publicId) {
        this.emitAlert('No plan selected', true)
        return
      }

      const checkoutPlan = this.planHelpers_getPaddleCheckoutPlan(publicId)
      if (!checkoutPlan) {
        this.emitAlert('Invalid plan configuration', true)
        return
      }

      this.dialog.loader = this.LOADER_STATES.SUBSCRIBE
      this.planMixin_subscribePlan(checkoutPlan, this.user, (success) => {
        this.dialog.loader = false
        this.closeBothDialogs()
        if (success) {
          this.emitAlert(`Successfully subscribed to ${this.planName}`)
          this.$emit('subscription-updated')
        } else {
          this.emitAlert('Subscription failed', true)
        }
      }, () => {
        // closeCallback - user closed popup
        this.dialog.loader = false
      })
    },

    // Pause subscription
    onPause() {
      if (!this.subscriptionId) return this.emitAlert('Subscription not found', true)

      this.showConfirmDialog(
        this.CONFIRM_ACTION_TYPES.PAUSE,
        (reason) => {
          this.confirmDialog.loader = true
          this.planMixin_resetSubscriptionStatus()
          this.subscriptionStatus.action = this.LOADER_STATES.PAUSE

          this.$store.dispatch('subscription/pauseSubscription', {
            id: this.subscriptionId,
            reason
          }).then(() => {
            this.planMixin_pollingSubscriptionStatus((success) => {
              this.confirmDialog.loader = false
              this.closeBothDialogs()
              if (success) {
                this.subscriptionStatus.success = true
                this.emitAlert('You have successfully paused your subscription.')
                this.$emit('subscription-updated')
              } else {
                this.emitAlert('Unable to confirm the paused subscription state.', true)
              }
            }, this.planHelpers_currentPlanKey, (user) => {
              const sub = user?.plan?.subscription
              return sub?.status === this.SUBSCRIPTION_STATUS.PAUSED ||
                sub?.paused_at ||
                sub?.paused_from ||
                user?.plan?.paused_from
            })
          }, (err) => {
            this.subscriptionStatus.error = true
            this.confirmDialog.loader = false
            this.closeBothDialogs()
            this.emitAlert('Unable to pause subscription. Please try again.', true)
          })
        }
      )
    },

    // Resume subscription
    onResume() {
      if (!this.subscriptionId) return this.emitAlert('Subscription not found', true)

      this.dialog.loader = this.LOADER_STATES.RESUME
      this.planMixin_resetSubscriptionStatus()
      this.subscriptionStatus.action = this.LOADER_STATES.RESUME

      this.$store.dispatch('subscription/resumeSubscription', {
        id: this.subscriptionId
      }).then(() => {
        this.planMixin_pollingSubscriptionStatus((success) => {
          this.dialog.loader = false
          this.closeBothDialogs()
          if (success) {
            this.subscriptionStatus.success = true
            this.emitAlert('You have successfully resumed your subscription.')
            this.$emit('subscription-updated')
          } else {
            this.emitAlert('Unable to confirm the resumed subscription state.', true)
          }
        }, this.planHelpers_currentPlanKey, (user) => {
          const sub = user?.plan?.subscription
          return sub?.status === this.SUBSCRIPTION_STATUS.ACTIVE &&
            !sub?.paused_at &&
            !sub?.paused_from &&
            !user?.plan?.paused_from
        })
      }, (err) => {
        this.subscriptionStatus.error = true
        this.dialog.loader = false
        this.closeBothDialogs()
        this.emitAlert('Unable to resume subscription. Please try again.', true)
      })
    },

    // Cancel subscription
    onCancel() {
      if (!this.subscriptionId) return this.emitAlert('Subscription not found', true)

      this.showConfirmDialog(
        this.CONFIRM_ACTION_TYPES.CANCEL,
        (reason) => {
          this.confirmDialog.loader = true
          this.planMixin_resetSubscriptionStatus()
          this.subscriptionStatus.action = 'cancel'

          this.$store.dispatch('subscription/cancelSubscription', {
            id: this.subscriptionId,
            reason
          }).then(() => {
            this.planMixin_pollingSubscriptionStatus((success) => {
              this.confirmDialog.loader = false
              this.closeBothDialogs()
              if (success) {
                this.subscriptionStatus.success = true
                this.emitAlert('You have successfully cancelled your subscription.')
                this.$emit('subscription-updated')
              } else {
                this.emitAlert('Unable to confirm the cancelled subscription state.', true)
              }
            }, this.planHelpers_currentPlanKey, (user) => {
              const sub = user?.plan?.subscription
              return sub?.status === this.SUBSCRIPTION_STATUS.DELETED ||
                sub?.cancelled_at ||
                sub?.cancel_scheduled_at ||
                user?.plan?.cancel_scheduled_at
            })
          }, (err) => {
            this.subscriptionStatus.error = true
            this.confirmDialog.loader = false
            this.closeBothDialogs()
            this.emitAlert('Unable to cancel subscription. Please try again.', true)
          })
        }
      )
    },

    // Removes the scheduled Billing cancellation and confirms the active state.
    onUndoCancellation() {
      if (!this.subscriptionId || !this.canUndoCancellation) {
        return this.emitAlert('Cancellation can no longer be reversed.', true)
      }

      this.dialog.loader = this.LOADER_STATES.UNDO_CANCEL
      this.planMixin_resetSubscriptionStatus()
      this.subscriptionStatus.action = this.LOADER_STATES.UNDO_CANCEL

      this.$store.dispatch('subscription/undoCancellation', {
        id: this.subscriptionId
      }).then(() => {
        this.planMixin_pollingSubscriptionStatus((success) => {
          this.dialog.loader = false
          this.closeBothDialogs()

          if (success) {
            this.subscriptionStatus.success = true
            this.emitAlert('Your cancellation has been reversed. Your subscription remains active.')
            this.$emit('subscription-updated')
          } else {
            this.emitAlert('Unable to confirm the active subscription state.', true)
          }
        }, this.planHelpers_currentPlanKey, (user) => {
          const sub = user?.plan?.subscription
          return sub?.status === this.SUBSCRIPTION_STATUS.ACTIVE &&
            !sub?.cancel_scheduled_at &&
            !user?.plan?.cancel_scheduled_at
        })
      }, (error) => {
        this.subscriptionStatus.error = true
        this.dialog.loader = false
        this.emitAlert(
          error?.response?.data?.meta?.error_message || 'Unable to reverse cancellation.',
          true
        )
      })
    },

    handleUpgradeDowngrade() {
      if (!this.selectedPlanKey || !this.subscriptionId) {
        this.emitAlert('Unable to fetch subscription details', true)
        return
      }

      const checkoutPlan = this.planHelpers_getPaddleCheckoutPlan(this.selectedPlanKey)
      if (!checkoutPlan) {
        this.emitAlert('Invalid plan selection', true)
        return
      }

      // Change plan directly without confirmation popup
      const actionType = this.planChangeType || LOADER_STATES.CHANGE
      this.dialog.loader = actionType
      this.planMixin_resetSubscriptionStatus()
      this.subscriptionStatus.action = actionType

      this.$store.dispatch('subscription/updateSubscription', {
        id: this.subscriptionId,
        params: { planId: checkoutPlan.priceId || checkoutPlan.productId || checkoutPlan.id }
      }).then(() => {
        this.planMixin_pollingSubscriptionStatus(() => {
          this.subscriptionStatus.success = true
          this.dialog.loader = false
          this.closeBothDialogs()
          const actionPastTense = actionType === LOADER_STATES.CHANGE ? 'changed' : `${actionType}d`
          this.emitAlert(`You have successfully ${actionPastTense} to this plan.`)
          this.$emit('subscription-updated')
        })
      }, (err) => {
        this.subscriptionStatus.error = true
        this.dialog.loader = false
        this.closeBothDialogs()
        this.emitAlert('Unable to change plan. Please try again.', true)
      })
    },
  }
}
</script>
